import { useState, useEffect } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';
import { MONTH_ORDER } from '../components/profitAndLoss/constants';
import type { BranchDeptLogic } from '../components/ProfitAndLossSearchFilters';

type UseHeadcountPlanParams = {
  fiscalYear: number;
  branchCodes: string[];
  departmentCodes: string[];
  // 拠点×部署の結合ロジック。staffRows 側の絞り込みと同じスコープになるよう共有する
  branchDeptLogic: BranchDeptLogic;
};

/**
 * 「従業員数 推移・増減」の予実対比に使う採用計画（計画採用）取得フック。
 *
 * 採用計画ページと同じ get_recruitment_plan を、先頭の有効予算バージョンで取得し、
 * 会計月ごとの計画採用人数 (planned_hires の合計) を返す。拠点・部署で絞り込み時は
 * staffRows と同じスコープに合わせてクライアント側で対象行を限定する。
 * 計画在籍は実績ベースライン依存のため算出せず、計画採用 (フロー) のみ提供する。
 */
export function useHeadcountPlan({ fiscalYear, branchCodes, departmentCodes, branchDeptLogic }: UseHeadcountPlanParams) {
  const [planHiresByMonth, setPlanHiresByMonth] = useState<Record<number, number>>({});
  const [planVersionName, setPlanVersionName] = useState<string>('');
  const [planLoading, setPlanLoading] = useState(false);
  // 計画採用が1件でも入力されているか (未保存なら全0 → 予実の注意表示に使う)
  const [hasPlan, setHasPlan] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchPlan = async () => {
      setPlanLoading(true);
      try {
        // 先頭の有効予算バージョンを採用計画の対象にする
        const vres = await axios.post(ep('get_active_budget_versions'), {});
        const versions = vres.data?.status === 'success' ? vres.data.data || [] : [];
        const version = versions[0];
        if (!version?.version_code) {
          if (!cancelled) { setPlanHiresByMonth({}); setPlanVersionName(''); setHasPlan(false); }
          return;
        }

        const res = await axios.post(ep('get_recruitment_plan'), {
          fiscal_year: fiscalYear,
          version_code: version.version_code,
        });
        const rows = res.data?.status === 'success' ? res.data.data || [] : [];

        const bset = new Set(branchCodes);
        const dset = new Set(departmentCodes);
        const byMonth: Record<number, number> = {};
        MONTH_ORDER.forEach((m) => (byMonth[m] = 0));
        let total = 0;
        // OR モード (拠点・部署とも指定時のみ) は「拠点一致 または 部署一致」で対象行を残す。
        // それ以外は従来どおり両条件の AND で絞り込む
        const useOr = branchDeptLogic === 'or' && branchCodes.length > 0 && departmentCodes.length > 0;
        rows.forEach((r: any) => {
          if (useOr) {
            if (!bset.has(r.branch) && !dset.has(r.department)) return;
          } else {
            if (branchCodes.length > 0 && !bset.has(r.branch)) return;
            if (departmentCodes.length > 0 && !dset.has(r.department)) return;
          }
          MONTH_ORDER.forEach((m) => {
            const h = Number(r[`month_${m}_hires`]) || 0;
            byMonth[m] += h;
            total += h;
          });
        });

        if (!cancelled) {
          setPlanHiresByMonth(byMonth);
          setPlanVersionName(version.version_name || version.version_code);
          setHasPlan(total > 0);
        }
      } catch (err) {
        console.error('採用計画(予実用)の取得エラー:', err);
        if (!cancelled) { setPlanHiresByMonth({}); setPlanVersionName(''); setHasPlan(false); }
      } finally {
        if (!cancelled) setPlanLoading(false);
      }
    };
    fetchPlan();
    return () => { cancelled = true; };
  }, [fiscalYear, branchCodes, departmentCodes, branchDeptLogic]);

  return { planHiresByMonth, planVersionName, planLoading, hasPlan };
}

export type UseHeadcountPlanResult = ReturnType<typeof useHeadcountPlan>;
